import React, { useEffect, useState, useRef } from 'react';
import { MessageSquare, Send, Phone, Video, Paperclip, Smile, CheckCheck, FileText, Info, Search } from 'lucide-react';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { ZodiacTheme } from './zodiacThemes';
import { VideoCallModal } from '../ui/VideoCallModal';
interface ChatMessage {
  id: number;
  text: string;
  isOwn: boolean;
  time: string;
  type?: 'text' | 'file';
  fileName?: string;
  fileSize?: string;
}
interface ChatContact {
  id: number;
  name: string;
  initials: string;
  lastMessage: string;
  time: string;
  unread: number;
  online: boolean;
  role: string;
}
interface ChatTabProps {
  theme: ZodiacTheme;
}
export function ChatTab({
  theme
}: ChatTabProps) {
  const [showCallModal, setShowCallModal] = useState(false);
  const [callMode, setCallMode] = useState<'video' | 'audio'>('video');
  const [activeChat, setActiveChat] = useState(0);
  const [chatMessage, setChatMessage] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [showProfile, setShowProfile] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const commonEmojis = ['😊', '👍', '❤️', '🙏', '✨', '🔮', '⭐', '💫', '🌙', '☀️', '🎯', '✅', '❌', '🤔', '😂', '🥰', '💜', '🙌', '👏', '💡'];
  const handleFileAttach = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const newMsg: ChatMessage = {
      id: Date.now(),
      text: file.name,
      isOwn: true,
      time: new Date().toLocaleTimeString([], {
        hour: '2-digit',
        minute: '2-digit'
      }),
      type: 'file',
      fileName: file.name,
      fileSize: `${(file.size / 1024).toFixed(1)} KB`
    };
    setMessagesMap(prev => ({
      ...prev,
      [activeChat]: [...(prev[activeChat] || []), newMsg]
    }));
    e.target.value = '';
  };
  const insertEmoji = (emoji: string) => {
    setChatMessage(prev => prev + emoji);
    setShowEmojiPicker(false);
  };
  const contacts: ChatContact[] = [{
    id: 0,
    name: 'Елена Смирнова',
    initials: 'ЕС',
    lastMessage: 'Подскажите, вам будет удобно созвониться?',
    time: '14:20',
    unread: 1,
    online: true,
    role: 'Астролог'
  }, {
    id: 1,
    name: 'Поддержка',
    initials: 'ПД',
    lastMessage: 'Ваш вопрос решён. Спасибо за обращение!',
    time: 'Вчера',
    unread: 0,
    online: true,
    role: 'Система'
  }, {
    id: 2,
    name: 'Мария Звёздная',
    initials: 'МЗ',
    lastMessage: 'Отчёт готов, отправляю файл.',
    time: '10 Мар',
    unread: 0,
    online: false,
    role: 'Астролог'
  }];
  const [messagesMap, setMessagesMap] = useState<Record<number, ChatMessage[]>>({
    0: [{
      id: 1,
      text: 'Здравствуйте, Анна! Я подготовила вашу карту для нашей консультации по синастрии. Подскажите, вам будет удобно созвониться в Zoom завтра в 14:00?',
      isOwn: false,
      time: '14:20'
    }, {
      id: 2,
      text: 'Добрый день, Елена! Да, завтра в 14:00 мне отлично подходит. Жду ссылку!',
      isOwn: true,
      time: '14:25'
    }],
    1: [{
      id: 1,
      text: 'Здравствуйте! У меня не отображается купленный отчёт.',
      isOwn: true,
      time: '10:00'
    }, {
      id: 2,
      text: 'Добрый день! Проверили — отчёт доступен в разделе «Мои покупки». Попробуйте обновить страницу.',
      isOwn: false,
      time: '10:15'
    }, {
      id: 3,
      text: 'Да, теперь вижу. Спасибо!',
      isOwn: true,
      time: '10:20'
    }, {
      id: 4,
      text: 'Ваш вопрос решён. Спасибо за обращение! Если будут ещё вопросы — пишите.',
      isOwn: false,
      time: '10:22'
    }],
    2: [{
      id: 1,
      text: 'Здравствуйте! Заказала натальную карту, когда будет готова?',
      isOwn: true,
      time: '09:00'
    }, {
      id: 2,
      text: 'Добрый день! Отчёт готов, отправляю файл.',
      isOwn: false,
      time: '12:30'
    }, {
      id: 3,
      text: 'Натальная_карта_Анна.pdf',
      isOwn: false,
      time: '12:31',
      type: 'file',
      fileName: 'Натальная_карта_Анна.pdf',
      fileSize: '3.1 MB'
    }]
  });
  const currentMessages = messagesMap[activeChat] || [];
  const currentContact = contacts[activeChat];
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({
      behavior: 'smooth'
    });
  }, [currentMessages, activeChat]);
  const handleSend = () => {
    if (!chatMessage.trim()) return;
    const newMsg: ChatMessage = {
      id: Date.now(),
      text: chatMessage,
      isOwn: true,
      time: new Date().toLocaleTimeString([], {
        hour: '2-digit',
        minute: '2-digit'
      })
    };
    setMessagesMap(prev => ({
      ...prev,
      [activeChat]: [...(prev[activeChat] || []), newMsg]
    }));
    setChatMessage('');
  };
  const filteredContacts = contacts.filter(c => c.name.toLowerCase().includes(searchQuery.toLowerCase()));
  return <div className="space-y-6 h-[calc(100vh-12rem)] min-h-[600px] flex flex-col" data-id="element-15784">
      <h2 className="text-2xl font-bold text-slate-900 mb-2" data-id="element-15785">Сообщения</h2>
      <Card padding="none" className="flex-grow flex overflow-hidden border border-slate-200" data-id="element-15786">
        {/* Contact List */}
        <div className="w-full md:w-1/3 border-r border-slate-200 flex flex-col bg-white" data-id="element-15787">
          <div className="p-3 border-b border-slate-100" data-id="element-15788">
            <div className="relative" data-id="element-15789">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" data-id="element-15790" />
              <input type="text" placeholder="Поиск сообщений..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-full pl-9 pr-4 py-2 text-sm border border-slate-200 rounded-xl focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary bg-slate-50" data-id="element-15791" />
            </div>
          </div>
          <div className="overflow-y-auto flex-grow" data-id="element-15792">
            {filteredContacts.map(contact => <div key={contact.id} onClick={() => setActiveChat(contact.id)} className={`p-3 border-b border-slate-50 cursor-pointer transition-colors ${activeChat === contact.id ? `${theme.bg}` : 'hover:bg-slate-50'}`} data-id="element-15793">
                <div className="flex items-start gap-3" data-id="element-15794">
                  <div className="relative flex-shrink-0" data-id="element-15795">
                    <div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-700 font-bold text-sm" data-id="element-15796">
                      {contact.initials}
                    </div>
                    {contact.online && <div className="absolute bottom-0 right-0 w-2.5 h-2.5 rounded-full bg-emerald-500 border-2 border-white" data-id="element-15797" />}
                  </div>
                  <div className="flex-grow min-w-0" data-id="element-15798">
                    <div className="flex justify-between items-baseline mb-0.5" data-id="element-15799">
                      <h4 className="font-bold text-slate-900 truncate text-sm" data-id="element-15800">
                        {contact.name}
                      </h4>
                      <span className="text-[10px] text-slate-500 flex-shrink-0 ml-2" data-id="element-15801">
                        {contact.time}
                      </span>
                    </div>
                    <div className="flex justify-between items-center" data-id="element-15802">
                      <p className="text-xs text-slate-500 truncate" data-id="element-15803">
                        {contact.lastMessage}
                      </p>
                      {contact.unread > 0 && <span className={`ml-2 w-5 h-5 rounded-full bg-gradient-to-r ${theme.gradient} text-white text-[10px] font-bold flex items-center justify-center flex-shrink-0`} data-id="element-15804">
                          {contact.unread}
                        </span>}
                    </div>
                  </div>
                </div>
              </div>)}
          </div>
        </div>

        {/* Chat Area */}
        <div className="hidden md:flex w-2/3 flex-col bg-slate-50" data-id="element-15805">
          {/* Header */}
          <div className="p-3 sm:p-4 border-b border-slate-200 bg-white flex justify-between items-center" data-id="element-15806">
            <div className="flex items-center gap-3" data-id="element-15807">
              <div className="relative" data-id="element-15808">
                <div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-700 font-bold" data-id="element-15809">
                  {currentContact.initials}
                </div>
                {currentContact.online && <div className="absolute bottom-0 right-0 w-2.5 h-2.5 rounded-full bg-emerald-500 border-2 border-white" data-id="element-15810" />}
              </div>
              <div data-id="element-15811">
                <h3 className="font-bold text-slate-900" data-id="element-15812">
                  {currentContact.name}
                </h3>
                <div className="text-xs flex items-center gap-1" data-id="element-15813">
                  {currentContact.online ? <span className="text-emerald-500 flex items-center" data-id="element-15814">
                      <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 mr-1" data-id="element-15815" />
                      В сети
                    </span> : <span className="text-slate-400" data-id="element-15816">Был(а) недавно</span>}
                  <span className="text-slate-300 mx-1" data-id="element-15817">·</span>
                  <span className="text-slate-400" data-id="element-15818">{currentContact.role}</span>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-1.5" data-id="element-15819">
              <button onClick={() => {
              setCallMode('audio');
              setShowCallModal(true);
            }} className="p-2.5 rounded-xl text-slate-500 hover:text-emerald-600 hover:bg-emerald-50 transition-colors" title="Аудиозвонок" data-id="element-15820">
                <Phone className="w-5 h-5" data-id="element-15821" />
              </button>
              <button onClick={() => {
              setCallMode('video');
              setShowCallModal(true);
            }} className="p-2.5 rounded-xl text-slate-500 hover:text-indigo-600 hover:bg-indigo-50 transition-colors" title="Видеозвонок" data-id="element-15822">
                <Video className="w-5 h-5" data-id="element-15823" />
              </button>
              <div className="w-px h-6 bg-slate-200 mx-1" data-id="element-15824" />
              <button onClick={() => setShowProfile(!showProfile)} className={`flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-medium transition-colors border ${showProfile ? 'text-indigo-700 bg-indigo-50 border-indigo-200' : 'text-slate-600 hover:bg-slate-50 border-slate-200'}`} data-id="element-15825">
                <Info className="w-3.5 h-3.5" data-id="element-15826" />
                {showProfile ? 'Скрыть' : 'Профиль'}
              </button>
            </div>
          </div>

          {/* Profile Panel */}
          {showProfile && <div className="px-4 py-3 bg-indigo-50/50 border-b border-indigo-100 flex items-center gap-4" data-id="element-15827">
              <div className="w-12 h-12 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-700 font-bold text-lg flex-shrink-0" data-id="element-15828">
                {currentContact.initials}
              </div>
              <div className="flex-1 min-w-0" data-id="element-15829">
                <div className="font-bold text-slate-900" data-id="element-15830">
                  {currentContact.name}
                </div>
                <div className="text-xs text-slate-500" data-id="element-15831">
                  {currentContact.role} ·{' '}
                  {currentContact.online ? 'В сети' : 'Офлайн'}
                </div>
              </div>
              <button onClick={() => setShowProfile(false)} className="p-1.5 text-slate-400 hover:text-slate-600 rounded-lg hover:bg-white transition-colors" data-id="element-15832">
                ✕
              </button>
            </div>}

          {/* Messages */}
          <div className="flex-grow overflow-y-auto p-4 sm:p-6 space-y-4 bg-slate-50/30" data-id="element-15833">
            <div className="flex justify-center my-2" data-id="element-15834">
              <span className="text-[10px] font-medium text-slate-400 bg-slate-100 px-3 py-1 rounded-full" data-id="element-15835">
                — Сегодня —
              </span>
            </div>
            {currentMessages.map(msg => <div key={msg.id} className={`flex ${msg.isOwn ? 'justify-end' : 'justify-start'}`} data-id="element-15836">
                <div className="flex items-end gap-2 max-w-[80%]" data-id="element-15837">
                  {!msg.isOwn && <div className="w-7 h-7 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-700 font-bold text-[10px] flex-shrink-0 mb-1" data-id="element-15838">
                      {currentContact.initials}
                    </div>}
                  <div data-id="element-15839">
                    {msg.type === 'file' ? <div className={`flex items-center gap-3 px-3 py-2 rounded-2xl ${msg.isOwn ? `bg-gradient-to-r ${theme.gradient} text-white rounded-tr-none` : 'bg-white border border-slate-200 text-slate-800 rounded-tl-none shadow-sm'}`} data-id="element-15840">
                        <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${msg.isOwn ? 'bg-white/20' : 'bg-indigo-50'}`} data-id="element-15841">
                          <FileText className={`w-4 h-4 ${msg.isOwn ? 'text-white' : 'text-indigo-600'}`} data-id="element-15842" />
                        </div>
                        <div className="min-w-0" data-id="element-15843">
                          <div className="text-sm font-medium truncate" data-id="element-15844">
                            {msg.fileName}
                          </div>
                          <div className={`text-xs ${msg.isOwn ? 'text-white/70' : 'text-slate-400'}`} data-id="element-15845">
                            {msg.fileSize}
                          </div>
                        </div>
                      </div> : <div className={`px-3 sm:px-4 py-2 rounded-2xl ${msg.isOwn ? `bg-gradient-to-r ${theme.gradient} text-white rounded-tr-none` : 'bg-white border border-slate-200 text-slate-800 rounded-tl-none shadow-sm'}`} data-id="element-15846">
                        <p className="text-sm" data-id="element-15847">{msg.text}</p>
                      </div>}
                    <div className={`flex items-center gap-1 mt-1 ${msg.isOwn ? 'justify-end mr-1' : 'ml-1'}`} data-id="element-15848">
                      <span className="text-[10px] text-slate-400" data-id="element-15849">
                        {msg.time}
                      </span>
                      {msg.isOwn && <CheckCheck className="w-3 h-3 text-indigo-400" data-id="element-15850" />}
                    </div>
                  </div>
                  {msg.isOwn && <div className={`w-7 h-7 rounded-full ${theme.bg} ${theme.text} flex items-center justify-center font-bold text-[10px] flex-shrink-0 mb-1`} data-id="element-15851">
                      А
                    </div>}
                </div>
              </div>)}
            <div ref={messagesEndRef} data-id="element-15852" />
          </div>

          {/* Input Area */}
          <div className="bg-white border-t border-slate-200" data-id="element-15853">
            {/* Hidden file input */}
            <input ref={fileInputRef} type="file" className="hidden" onChange={handleFileAttach} data-id="element-15854" />

            {/* Emoji Picker */}
            {showEmojiPicker && <div className="px-3 pt-3" data-id="element-15855">
                <div className="p-3 bg-slate-50 border border-slate-200 rounded-xl" data-id="element-15856">
                  <div className="flex justify-between items-center mb-2" data-id="element-15857">
                    <span className="text-xs font-bold text-slate-700" data-id="element-15858">
                      Эмодзи
                    </span>
                    <button onClick={() => setShowEmojiPicker(false)} className="text-slate-400 hover:text-slate-600 text-xs" data-id="element-15859">
                      ✕
                    </button>
                  </div>
                  <div className="grid grid-cols-10 gap-1" data-id="element-15860">
                    {commonEmojis.map(emoji => <button key={emoji} onClick={() => insertEmoji(emoji)} className="w-8 h-8 flex items-center justify-center text-lg hover:bg-indigo-50 rounded-lg transition-colors" data-id="element-15861">
                        {emoji}
                      </button>)}
                  </div>
                </div>
              </div>}

            <div className="p-3 sm:p-4 flex items-center gap-2" data-id="element-15862">
              <button onClick={() => fileInputRef.current?.click()} className="p-2 text-slate-400 hover:text-indigo-600 rounded-full hover:bg-slate-100 transition-colors flex-shrink-0" title="Прикрепить файл" data-id="element-15863">
                <Paperclip className="w-5 h-5" data-id="element-15864" />
              </button>
              <input type="text" placeholder="Написать сообщение..." value={chatMessage} onChange={e => setChatMessage(e.target.value)} onKeyDown={e => {
              if (e.key === 'Enter' && chatMessage.trim()) {
                handleSend();
              }
            }} className="flex-grow bg-slate-50 border border-slate-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary" data-id="element-15865" />
              <button onClick={() => setShowEmojiPicker(!showEmojiPicker)} className={`p-2 rounded-full transition-colors flex-shrink-0 ${showEmojiPicker ? 'text-amber-500 bg-amber-50' : 'text-slate-400 hover:text-amber-500 hover:bg-slate-100'}`} title="Эмодзи" data-id="element-15866">
                <Smile className="w-5 h-5" data-id="element-15867" />
              </button>
              <button onClick={handleSend} disabled={!chatMessage.trim()} className={`w-10 h-10 rounded-full bg-gradient-to-r ${theme.gradient} text-white flex items-center justify-center transition-opacity hover:opacity-90 flex-shrink-0 disabled:opacity-50 disabled:cursor-not-allowed`} data-id="element-15868">
                <Send className="w-4 h-4 ml-0.5" data-id="element-15869" />
              </button>
            </div>
          </div>
        </div>
      </Card>

      {/* Video Call Modal */}
      <VideoCallModal isOpen={showCallModal} onClose={() => setShowCallModal(false)} mode={callMode} participantName={currentContact.name} participantInitials={currentContact.initials} onEnd={() => {}} data-id="element-15870" />
    </div>;
}